/**
 * Passos "Como sua loja funciona" e "Horários" do onboarding: tradução do formulário curto para o que o
 * painel já grava (formas de entrega no molde do wizard v2 e horários do serviço `site`). Funções puras.
 */
export class ErroDadosOnboarding extends Error {}

export interface DadosLojaOnboarding {
  delivery: boolean;
  retirada: boolean;
  /** endereço já salvo (texto livre) continua valendo: não pede rua e número de novo */
  manterEndereco?: boolean;
  cep?: string;
  rua: string;
  numero: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  taxa?: number;
  tempoMinimo?: number;
  tempoMaximo?: number;
  tempoRetiradaMinimo?: number;
  tempoRetiradaMaximo?: number;
  pedidoMinimo?: number;
  /** só existe com retirada: na loja vira a opção "Comer no local" da forma Retirar */
  comerNoLocal?: boolean;
  agendamento?: boolean;
  /** só vem quando o dono mexeu no horário dentro do passo; sem ele, o horário salvo não muda */
  horario?: HorarioOnboarding;
}

export interface TurnoOnboarding { abre: string; fecha: string; }

/** Mesmos turnos em todos os dias marcados (0 = domingo): o que o passo Loja consegue editar sem a tela de Horários. */
export interface HorarioOnboarding { dias: number[]; turnos: TurnoOnboarding[]; }

export interface LojaAtualOnboarding {
  delivery: boolean;
  retirada: boolean;
  taxa: number | null;
  tempoMinimo: number | null;
  tempoMaximo: number | null;
  tempoRetiradaMinimo: number | null;
  tempoRetiradaMaximo: number | null;
  pedidoMinimo: number | null;
  comerNoLocal: boolean;
  agendamento: boolean;
  endereco: string;
  cep: string;
}

/** `outrosTurnos` só aparece quando o dia tem mais de um turno (ex.: almoço e jantar). */
export interface DiaDaGrade { dia: number; aberto: boolean; abre: string; fecha: string; outrosTurnos?: TurnoOnboarding[]; }

const RETIRAR = 1;
const ENTREGA = 2;

function numero(valor: any): number | null {
  if (valor === null || valor === undefined || valor === '') return null;
  const n = Number(String(valor).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function formaAtual(formas: any[], id: number): any {
  return (formas || []).find((f: any) => Number(f?.formaDeEntrega?.id) === id) || null;
}

/** O que já está configurado, para o passo abrir preenchido (inclusive o que a Gestor fez pelo WhatsApp). */
export function lojaAtual(empresa: any): LojaAtualOnboarding {
  const retirada = formaAtual(empresa?.formasDeEntrega, RETIRAR);
  const entrega = formaAtual(empresa?.formasDeEntrega, ENTREGA);
  return {
    delivery: !!entrega?.ativa,
    retirada: !!retirada?.ativa,
    taxa: entrega ? numero(entrega.taxaFixa) : null,
    tempoMinimo: entrega ? numero(entrega.tempoMinimo) : null,
    tempoMaximo: entrega ? numero(entrega.tempoMaximo) : null,
    tempoRetiradaMinimo: retirada ? numero(retirada.tempoMinimoRetirada) : null,
    tempoRetiradaMaximo: retirada ? numero(retirada.tempoMaximoRetirada) : null,
    pedidoMinimo: entrega ? numero(entrega.valorMinimoPedido) : null,
    comerNoLocal: !!retirada?.ativa && !!retirada?.permiteComerNoLocal,
    agendamento: [retirada, entrega].some(f => f?.ativa && f?.permiteAgendamento),
    endereco: String(empresa?.endereco || ''),
    cep: String(empresa?.cep || '')
  };
}

export function valideDadosLoja(dados: DadosLojaOnboarding): DadosLojaOnboarding {
  if (!dados || (!dados.delivery && !dados.retirada)) throw new ErroDadosOnboarding('Escolha entrega, retirada ou os dois.');
  const manterEndereco = !!dados.manterEndereco;
  const rua = String(dados.rua || '').trim();
  const num = String(dados.numero || '').trim();
  if (!manterEndereco && !rua) throw new ErroDadosOnboarding('Informe a rua da loja.');
  if (!manterEndereco && !num) throw new ErroDadosOnboarding('Informe o número da loja (ou "s/n").');
  const limpo: DadosLojaOnboarding = {
    delivery: !!dados.delivery, retirada: !!dados.retirada, manterEndereco, rua, numero: num,
    cep: String(dados.cep || '').replace(/\D/g, '').slice(0, 8),
    complemento: String(dados.complemento || '').trim().slice(0, 60),
    bairro: String(dados.bairro || '').trim().slice(0, 80),
    cidade: String(dados.cidade || '').trim().slice(0, 80),
    estado: String(dados.estado || '').trim().toUpperCase().slice(0, 2)
  };
  if (dados.comerNoLocal !== undefined) limpo.comerNoLocal = limpo.retirada && !!dados.comerNoLocal;
  if (dados.agendamento !== undefined) limpo.agendamento = !!dados.agendamento;
  if (dados.horario) limpo.horario = valideHorario(dados.horario);
  if (limpo.delivery) {
    const taxa = numero(dados.taxa);
    if (taxa === null || taxa < 0) throw new ErroDadosOnboarding('Informe a taxa de entrega (pode ser 0).');
    const min = numero(dados.tempoMinimo), max = numero(dados.tempoMaximo);
    if (!min || !max || min < 1 || max < min) throw new ErroDadosOnboarding('Informe o tempo de entrega: de quantos a quantos minutos.');
    const pedidoMinimo = numero(dados.pedidoMinimo);
    if (pedidoMinimo !== null && pedidoMinimo < 0) throw new ErroDadosOnboarding('Pedido mínimo não pode ser negativo.');
    Object.assign(limpo, {taxa, tempoMinimo: Math.round(min), tempoMaximo: Math.round(max), pedidoMinimo: pedidoMinimo || 0});
  }
  if (limpo.retirada) {
    const min = numero(dados.tempoRetiradaMinimo), max = numero(dados.tempoRetiradaMaximo);
    if (!min || !max || min < 1 || max < min) throw new ErroDadosOnboarding('Informe em quanto tempo o pedido fica pronto para retirar.');
    Object.assign(limpo, {tempoRetiradaMinimo: Math.round(min), tempoRetiradaMaximo: Math.round(max)});
  }
  return limpo;
}

/** "Rua 9, 120 - Sala 2 - Setor Oeste, Goiânia/GO": empresa.endereco é texto livre. */
export function monteEndereco(d: DadosLojaOnboarding): string {
  let texto = `${d.rua}, ${d.numero}`;
  if (d.complemento) texto += ` - ${d.complemento}`;
  if (d.bairro) texto += ` - ${d.bairro}`;
  if (d.cidade) texto += `, ${d.cidade}${d.estado ? '/' + d.estado : ''}`;
  return texto;
}

/**
 * Formas de entrega no molde do wizard v2 (id 1 = retirar, 2 = entrega), preservando o resto da configuração
 * que já existia. Entrega sempre por taxa fixa: bairro e distância se ajustam depois em Formas de entrega.
 */
export function monteFormasDeEntrega(atuais: any[], d: DadosLojaOnboarding): any[] {
  const retiradaAtual = formaAtual(atuais, RETIRAR) || {};
  const entregaAtual = formaAtual(atuais, ENTREGA) || {};
  const retirada = Object.assign({
    nome: 'Retirar', valorMinimoPedido: 0, possuiDesconto: false, descontoPercentual: 0,
    possuiDescontoComerNoLocal: false, descontoPercentualComerNoLocal: 0, raiosDeCobranca: [], zonasDeEntrega: []
  }, retiradaAtual, {
    formaDeEntrega: Object.assign({}, retiradaAtual.formaDeEntrega, {id: RETIRAR}),
    nome: 'Retirar',
    ativa: d.retirada
  });
  if (d.retirada) Object.assign(retirada, {tempoMinimoRetirada: d.tempoRetiradaMinimo, tempoMaximoRetirada: d.tempoRetiradaMaximo});
  if (d.comerNoLocal !== undefined) retirada.permiteComerNoLocal = d.comerNoLocal;

  const entrega = Object.assign({
    nome: 'Receber em casa', valorMinimoPedido: 0, valorMinimoFreteGratis: 0, exibirTelaBusca: true,
    possuiDesconto: false, descontoPercentual: 0, raiosDeCobranca: [], zonasDeEntrega: []
  }, entregaAtual, {
    formaDeEntrega: Object.assign({}, entregaAtual.formaDeEntrega, {id: ENTREGA}),
    nome: 'Receber em casa',
    ativa: d.delivery
  });
  if (d.delivery) Object.assign(entrega, {tipoDeCobranca: 'fixo', taxaFixa: d.taxa, tempoMinimo: d.tempoMinimo,
    tempoMaximo: d.tempoMaximo, valorMinimoPedido: d.pedidoMinimo || 0});
  // agendamento vale para as formas ligadas; o intervalo vazio vira 30 min, como no cadastro de formas de entrega
  if (d.agendamento !== undefined) {
    for (const forma of [retirada, entrega].filter(f => f.ativa)) {
      forma.permiteAgendamento = d.agendamento;
      if (d.agendamento && !Number(forma.intervaloAgendamento)) forma.intervaloAgendamento = 30;
    }
  }
  return [retirada, entrega];
}

function hora(valor: any): string {
  return String(valor || '').slice(0, 5);
}

/** Grade da semana a partir dos horários do serviço `site` (todos os turnos do dia, em ordem). */
export function gradeAtual(horarios: any[]): DiaDaGrade[] {
  const doSite = (horarios || []).filter((h: any) => (!h.servico || h.servico === 'site') && h.funciona);
  return [0, 1, 2, 3, 4, 5, 6].map(dia => {
    const doDia = doSite.filter((h: any) => Number(h.diaDaSemana) === dia)
      .sort((a: any, b: any) => hora(a.horarioAbertura).localeCompare(hora(b.horarioAbertura)));
    if (!doDia.length) return {dia, aberto: false, abre: '18:00', fecha: '23:00'};
    const d: DiaDaGrade = {dia, aberto: true, abre: hora(doDia[0].horarioAbertura), fecha: hora(doDia[0].horarioFechamento)};
    if (doDia.length > 1) d.outrosTurnos = doDia.slice(1).map((h: any) => ({abre: hora(h.horarioAbertura), fecha: hora(h.horarioFechamento)}));
    return d;
  });
}

function turnosDoDia(d: DiaDaGrade): TurnoOnboarding[] {
  return [{abre: d.abre, fecha: d.fecha}, ...(d.outrosTurnos || [])];
}

/**
 * Horários para EmpresaService.salveHorarioFuncionamento: os do `site` que existiam saem (removido) e entra
 * um registro por turno de cada dia aberto. Fechar depois da meia-noite (18:00–01:00) o domínio já trata como madrugada.
 */
export function monteHorarios(existentes: any[], grade: DiaDaGrade[]): any[] {
  const remover = (existentes || []).filter((h: any) => h.id && (!h.servico || h.servico === 'site'))
    .map((h: any) => ({id: h.id, servico: 'site', diaDaSemana: h.diaDaSemana, removido: true}));
  // sem flatMap: o painel também importa este arquivo e compila com lib anterior ao es2019
  const novos = grade.filter(d => d.aberto).reduce((lista: any[], d) => lista.concat(turnosDoDia(d).map(t => ({servico: 'site',
    diaDaSemana: d.dia, horarioAbertura: `${t.abre}:00`, horarioFechamento: `${t.fecha}:00`, funciona: true}))), []);
  return [...remover, ...novos];
}

/** A semana do passo Loja (mesmos turnos nos dias marcados) na grade de 7 dias. */
export function gradeDoHorario(h: HorarioOnboarding): DiaDaGrade[] {
  const [primeiro, ...outros] = h.turnos;
  return [0, 1, 2, 3, 4, 5, 6].map(dia => {
    if (!h.dias.includes(dia)) return {dia, aberto: false, abre: primeiro.abre, fecha: primeiro.fecha};
    const d: DiaDaGrade = {dia, aberto: true, abre: primeiro.abre, fecha: primeiro.fecha};
    if (outros.length) d.outrosTurnos = outros.map(t => ({...t}));
    return d;
  });
}

/** O inverso: só quando todos os dias abertos têm os mesmos turnos. Grade feita dia a dia devolve null. */
export function horarioDaGrade(grade: DiaDaGrade[]): HorarioOnboarding | null {
  const abertos = (grade || []).filter(d => d.aberto);
  if (!abertos.length) return null;
  const turnos = turnosDoDia(abertos[0]);
  if (abertos.some(d => JSON.stringify(turnosDoDia(d)) !== JSON.stringify(turnos))) return null;
  return {dias: abertos.map(d => d.dia), turnos};
}

const HORA = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Um ou dois turnos; com dois, o segundo começa depois que o primeiro fecha (só o último pode passar da meia-noite). */
export function valideHorario(h: any): HorarioOnboarding {
  const dias = Array.from(new Set<number>((h?.dias || []).map((d: any) => Number(d)))).filter(d => Number.isInteger(d) && d >= 0 && d <= 6).sort();
  if (!dias.length) throw new ErroDadosOnboarding('Marque os dias em que a loja abre.');
  const turnos: TurnoOnboarding[] = (h?.turnos || []).map((t: any) => ({abre: hora(t?.abre), fecha: hora(t?.fecha)}));
  if (!turnos.length || turnos.length > 2) throw new ErroDadosOnboarding('Escolha o horário da loja.');
  for (const t of turnos) {
    if (!HORA.test(t.abre) || !HORA.test(t.fecha)) throw new ErroDadosOnboarding('Horário inválido: use horas como 18:00.');
    if (t.abre === t.fecha) throw new ErroDadosOnboarding('O horário de abrir e o de fechar não podem ser iguais.');
    // mesma regra de HorarioFuncionamento.valide: passar da meia-noite só até as 5h59
    if (t.fecha < t.abre && t.fecha >= '06:00') throw new ErroDadosOnboarding('Fechar depois da meia-noite vale até as 5h59.');
  }
  if (turnos.length === 2 && !(turnos[0].abre < turnos[0].fecha && turnos[0].fecha < turnos[1].abre)) {
    throw new ErroDadosOnboarding('O segundo turno precisa começar depois que o primeiro fecha.');
  }
  return {dias, turnos};
}

/**
 * Horário com que a loja começa, pelo segmento: o onboarding não pergunta horário; o dono ajusta depois em Horários.
 * `dias` = dias da semana abertos (0 = domingo). A ordem importa: o primeiro que casar vence ("pizzaria e restaurante").
 */
const HORARIOS_POR_SEGMENTO: {rotulo: string; palavras: RegExp; dias: number[]; abre: string; fecha: string}[] = [
  {rotulo: 'pizzaria', palavras: /pizz/, dias: [0, 2, 3, 4, 5, 6], abre: '18:00', fecha: '23:30'},
  {rotulo: 'hamburgueria e lanches', palavras: /hamb|burg|lanch|hot ?dog|pastel|espet/, dias: [0, 2, 3, 4, 5, 6], abre: '18:00', fecha: '23:30'},
  {rotulo: 'comida japonesa', palavras: /japon|sushi|temaki|poke/, dias: [0, 2, 3, 4, 5, 6], abre: '18:00', fecha: '23:00'},
  {rotulo: 'açaí e sorvete', palavras: /acai|sorve|gelat|milk ?shake/, dias: [0, 1, 2, 3, 4, 5, 6], abre: '13:00', fecha: '22:00'},
  {rotulo: 'doces e salgados', palavras: /doce|confeit|bolo|brigadeir|salgad/, dias: [1, 2, 3, 4, 5, 6], abre: '09:00', fecha: '18:00'},
  {rotulo: 'padaria e café', palavras: /padar|panific|\bcafe/, dias: [0, 1, 2, 3, 4, 5, 6], abre: '07:00', fecha: '20:00'},
  {rotulo: 'restaurante e marmitaria', palavras: /restaur|marmit|almoc|self|comida|prato feito|executiv|saudav|salada/, dias: [1, 2, 3, 4, 5, 6], abre: '11:00', fecha: '15:00'}
];
const HORARIO_SEM_SEGMENTO = {rotulo: 'delivery à noite', dias: [0, 2, 3, 4, 5, 6], abre: '18:00', fecha: '23:00'};

/** Todas as semanas que o sistema grava sozinho como padrão (por tipo de comida, por segmento ou sem segmento). */
export function gradesPadrao(): DiaDaGrade[][] {
  return [...HORARIOS_POR_SEGMENTO, HORARIO_SEM_SEGMENTO].map(h =>
    [0, 1, 2, 3, 4, 5, 6].map(dia => ({dia, aberto: h.dias.includes(dia), abre: h.abre, fecha: h.fecha})));
}

function semAcento(texto: any): string {
  return String(texto || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/**
 * O tipo de comida que o dono escolheu em "Conhecendo seu negócio" vence; depois o segmento cadastrado na empresa; sem
 * eles, o que o dono contou para a Mia na conversa.
 */
export function horarioPadraoDoSegmento(segmentoEmpresa: any, segmentoDaConversa: any, tipoDeComida?: any): {rotulo: string; grade: DiaDaGrade[]} {
  const texto = `${semAcento(segmentoEmpresa)} | ${semAcento(segmentoDaConversa)}`;
  const casa = (valor: any) => HORARIOS_POR_SEGMENTO.find(h => h.palavras.test(semAcento(valor)));
  const escolhido = casa(tipoDeComida) || casa(segmentoEmpresa) || HORARIOS_POR_SEGMENTO.find(h => h.palavras.test(texto)) || HORARIO_SEM_SEGMENTO;
  return {rotulo: escolhido.rotulo, grade: [0, 1, 2, 3, 4, 5, 6].map(dia =>
    ({dia, aberto: escolhido.dias.includes(dia), abre: escolhido.abre, fecha: escolhido.fecha}))};
}

/** "Ter a dom, 18:00 às 23:30": dias seguidos com o mesmo horário juntam, a semana começa na segunda. */
export function resumoDaGrade(grade: DiaDaGrade[]): string {
  const nomes = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
  const semana = [1, 2, 3, 4, 5, 6, 0].map(n => (grade || []).find(d => d.dia === n)).filter(d => d && d.aberto);
  if (!semana.length) return 'Fechada todos os dias';
  const blocos: {de: number; ate: number; horario: string}[] = [];
  const posicao = (dia: number) => (dia + 6) % 7;
  for (const d of semana) {
    const horario = turnosDoDia(d).map(t => `${t.abre} às ${t.fecha}`).join(' e ');
    const ultimo = blocos[blocos.length - 1];
    if (ultimo && ultimo.horario === horario && posicao(d.dia) === posicao(ultimo.ate) + 1) ultimo.ate = d.dia;
    else blocos.push({de: d.dia, ate: d.dia, horario});
  }
  return blocos.map(b => {
    const dias = b.de === b.ate ? nomes[b.de] : `${nomes[b.de]} ${posicao(b.ate) - posicao(b.de) === 1 ? 'e' : 'a'} ${nomes[b.ate]}`;
    return `${dias.charAt(0).toUpperCase()}${dias.slice(1)}, ${b.horario}`;
  }).join(' · ');
}
